// Mirrors the live queue into storage and brings it back after a reload.
// Mounted once, from the persistent player. See session.ts for the storage contract.
import { useEffect, useRef } from 'react';
import { appleMusicProvider } from '../lib/appleMusicProvider';
import type { usePlayer } from './usePlayer';
import { clearSession, loadSession, saveSession, sessionTrackToItem, toSaved } from './session';

const provider = appleMusicProvider();
const CONNECTED = new Set(['connected_eligible', 'connected_no_subscription']);
const SAVE_EVERY_MS = 5000;

export function useSessionPersistence(p: ReturnType<typeof usePlayer>): void {
  const { authState } = p.ui;
  const restoreDone = useRef(false);
  const lastSaveAt = useRef(0);
  const lastTrackId = useRef<string | null>(null);
  const lastSignature = useRef('');

  // ---- restore: once, as soon as a connection exists and nothing is already playing ----
  useEffect(() => {
    if (restoreDone.current || !CONNECTED.has(authState)) return;
    restoreDone.current = true;
    const saved = loadSession();
    if (!saved) return;
    // Something already playing (a fresh click beat the restore) — leave it alone.
    if (provider.snapshot().item) return;
    void p.restoreQueue(saved.tracks.map(sessionTrackToItem), saved.index, saved.positionMs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState]);

  // ---- save: follow the live queue, throttled, plus immediately on a track change ----
  useEffect(() => {
    const persist = () => {
      const { items, current } = provider.queueItems();
      if (!items.length) return;
      const snap = provider.snapshot();
      const trackId = snap.item?.id ?? null;
      const now = Date.now();
      const changedTrack = trackId !== lastTrackId.current;
      if (!changedTrack && now - lastSaveAt.current < SAVE_EVERY_MS) return;
      // Index by the playing track when MusicKit reports it; the queue position otherwise.
      const byId = trackId ? items.findIndex((i) => i.id === trackId) : -1;
      const index = byId >= 0 ? byId : current;
      // Paused and untouched? Nothing to write — the subscription ticks once a second.
      const signature = `${items.length}:${trackId}:${index}:${snap.positionMs}`;
      if (signature === lastSignature.current) return;
      lastSignature.current = signature;
      lastTrackId.current = trackId;
      lastSaveAt.current = now;
      saveSession({ tracks: toSaved(items), index, positionMs: snap.positionMs });
    };
    const unsubscribe = provider.subscribe(persist);
    return unsubscribe;
  }, []);

  // A signed-out / disconnected listener should not find someone else's queue waiting.
  useEffect(() => {
    if (authState === 'not_connected' && restoreDone.current) clearSession();
  }, [authState]);
}
